import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Alert,
    CircularProgress,
    Avatar,
    Paper,
    FormControlLabel,
    Checkbox,
    Menu
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Palette as PaletteIcon,
    MoreVert as MoreVertIcon,
    InfoOutlined as InfoOutlinedIcon,
    MenuBook as MenuBookIcon
} from '@mui/icons-material';
import GuideEditorManager from '../Admin/GuideEditorManager';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
    getAllArchetypes,
    createArchetype,
    updateArchetype,
    deleteArchetype,
    mergeArchetypes,
    copyArchetypesToScope,
    assignLegacyArchetypesScope,
    getExpansionSets,
    getArchetypeRankings,
    setArchetypeRanking
} from '../../services/tracker-limited-data';
import { getAllInkColors } from './InkColorIcon';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from './ConfirmDialog';

const TIER_DEFINITIONS = [
    { name: 'gauntlet', label: 'Gauntlet', color: '#ef4444', order: 0, description: 'Format-defining archetyp, który trzeba testować w pierwszej kolejności.' },
    { name: 'challenger', label: 'Challenger', color: '#f97316', order: 1, description: 'Bardzo mocny wybór, regularnie walczący z topem mety.' },
    { name: 'contender', label: 'Contender', color: '#eab308', order: 2, description: 'Stabilny archetyp turniejowy, ale zależny od konkretnej mety.' },
    { name: 'rogue', label: 'Rogue', color: '#22c55e', order: 3, description: 'Niszowy/off-meta pick, zwykle celowany w konkretny field.' },
    { name: 'not_tested', label: 'Not tested', color: '#64748b', order: 4, description: 'Brak oceny użytkownika. Nie jest wliczany do rankingu globalnego.' }
];

function ArchetypeManager({ onOpenDialog, onCloseDialog, dialogOpen: externalDialogOpen, canManageArchetypes = false, trackerFilters }) {
    const { userData, currentUser } = useAuth();
    const navigate = useNavigate();
    const [archetypes, setArchetypes] = useState([]);
    const [userTierlist, setUserTierlist] = useState({});
    const [globalTierlist, setGlobalTierlist] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingArchetype, setEditingArchetype] = useState(null);

    // Synchronizuj dialogOpen z props
    React.useEffect(() => {
        if (externalDialogOpen !== undefined) {
            setDialogOpen(externalDialogOpen);
        }
    }, [externalDialogOpen]);
    const [formData, setFormData] = useState({
        name: '',
        ink_colors: []
    });
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [mergeSourceId, setMergeSourceId] = useState('');
    const [mergeTargetId, setMergeTargetId] = useState('');
    const [mergeReason, setMergeReason] = useState('');
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copySubmitting, setCopySubmitting] = useState(false);
    const [expansionSets, setExpansionSets] = useState([]);
    const [copySourceFormat, setCopySourceFormat] = useState('core_constructed');
    const [copySourceSetId, setCopySourceSetId] = useState('');
    const [copyOverwrite, setCopyOverwrite] = useState(false);
    const [legacyAssigning, setLegacyAssigning] = useState(false);
    const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);
    const [confirmDeleteArchetype, setConfirmDeleteArchetype] = useState({ open: false, archetypeId: null, archetypeName: '' });
    const [tierListView, setTierListView] = useState('personal');
    const [rankingsAvailable, setRankingsAvailable] = useState(true);
    const suppressCardClickUntilRef = useRef(0);
    const dragPortalRef = useRef(null);
    const [guideEditorManagerOpen, setGuideEditorManagerOpen] = useState(false);
    const [guideEditorManagerArchetype, setGuideEditorManagerArchetype] = useState(null);

    // Kolory atramentu w Lorcana z ikonami
    const inkColors = getAllInkColors();

    // Sprawdź czy użytkownik ma uprawnienia administratora
    const isAdmin = (userData?.role === 'admin' || userData?.role === 'superadmin') && canManageArchetypes;
    const isSuperAdmin = userData?.role === 'superadmin' && canManageArchetypes;

    // Definicja tierów
    const tiers = TIER_DEFINITIONS;
    const tierNameToLabel = Object.fromEntries(TIER_DEFINITIONS.map((tier) => [tier.name, tier.label]));

    const normalizeTierName = useCallback((value) => {
        if (typeof value !== 'string') return 'not_tested';
        const trimmed = value.trim();
        if (!trimmed) return 'not_tested';

        const canonical = trimmed
            .toLowerCase()
            .replace(/-/g, '_')
            .replace(/\s+/g, '_');

        if (TIER_DEFINITIONS.some((tier) => tier.name === canonical)) {
            return canonical;
        }

        const byLabel = TIER_DEFINITIONS.find((tier) => tier.label.toLowerCase() === trimmed.toLowerCase());
        return byLabel?.name || 'not_tested';
    }, []);

    // Ładowanie danych przy inicjalizacji
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [archetypesResult, rankingsResult] = await Promise.allSettled([
                    getAllArchetypes(trackerFilters),
                    getArchetypeRankings(trackerFilters)
                ]);

                if (archetypesResult.status !== 'fulfilled') {
                    throw archetypesResult.reason;
                }

                const archetypesData = archetypesResult.value || [];
                const rankingsData = rankingsResult.status === 'fulfilled'
                    ? rankingsResult.value
                    : null;
                setRankingsAvailable(rankingsResult.status === 'fulfilled');

                setArchetypes(archetypesData);
                const rawUserRankings = rankingsData?.userRankings || {};
                const normalizedUserRankings = Object.fromEntries(
                    Object.entries(rawUserRankings).map(([archetypeId, tierName]) => [
                        archetypeId,
                        normalizeTierName(tierName)
                    ])
                );
                setUserTierlist(normalizedUserRankings);
                setGlobalTierlist(rankingsData?.globalRankings || {});

                if (rankingsResult.status === 'rejected') {
                    console.warn('Ranking archetypów niedostępny w tym środowisku:', rankingsResult.reason);
                    setTierListView('global');
                }
            } catch (err) {
                console.error('Błąd podczas ładowania danych:', err);
                setError('Nie udało się załadować danych. Spróbuj ponownie.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser, trackerFilters, normalizeTierName]);

    useEffect(() => {
        if (!isAdmin) return;
        getExpansionSets()
            .then((sets) => {
                setExpansionSets(sets || []);
                if (!copySourceSetId && sets?.length) {
                    setCopySourceSetId(sets[sets.length - 1].id);
                }
            })
            .catch(() => setExpansionSets([]));
    }, [isAdmin, copySourceSetId]);

    const handleOpenDialog = (archetype = null) => {
        // Jeśli próbuje edytować archetyp, sprawdź uprawnienia administratora
        if (archetype && !isAdmin) {
            setError('Tylko administratorzy mogą edytować archetypy.');
            return;
        }

        if (archetype) {
            setEditingArchetype(archetype);
            setFormData({
                name: archetype.name,
                ink_colors: archetype.ink_colors || []
            });
        } else {
            setEditingArchetype(null);
            setFormData({
                name: '',
                ink_colors: []
            });
        }
        if (onOpenDialog) {
            onOpenDialog();
        } else {
            setDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        if (onCloseDialog) {
            onCloseDialog();
        } else {
            setDialogOpen(false);
        }
        setEditingArchetype(null);
        setFormData({
            name: '',
            ink_colors: []
        });
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSubmit = async () => {
        try {
            setError(null);

            if (editingArchetype) {
                // Edit existing archetype
                await updateArchetype(editingArchetype.id, formData);

                // Refresh archetypes list
                const updatedArchetypes = await getAllArchetypes(trackerFilters);
                setArchetypes(updatedArchetypes);
            } else {
                // Add new archetype
                await createArchetype({
                    ...formData,
                    formatScope: trackerFilters?.format || null,
                    setScope: trackerFilters?.setId || null
                });

                // Refresh archetypes list
                const updatedArchetypes = await getAllArchetypes(trackerFilters);
                setArchetypes(updatedArchetypes);
            }

            handleCloseDialog();
        } catch (err) {
            console.error('Błąd podczas zapisywania archetypu:', err);
            setError('Nie udało się zapisać archetypu. Spróbuj ponownie.');
        }
    };

    const handleDeleteArchetype = async (archetypeId) => {
        // Sprawdź uprawnienia administratora
        if (!isAdmin) {
            setError('Tylko administratorzy mogą usuwać archetypy.');
            return;
        }

        try {
            setError(null);
            await deleteArchetype(archetypeId);

            // Refresh archetypes list
            const updatedArchetypes = await getAllArchetypes(trackerFilters);
            setArchetypes(updatedArchetypes);
        } catch (err) {
            console.error('Błąd podczas usuwania archetypu:', err);
            setError('Nie udało się usunąć archetypu. Spróbuj ponownie.');
        }
    };

    const requestDeleteArchetype = (archetype) => {
        setConfirmDeleteArchetype({
            open: true,
            archetypeId: archetype?.id || null,
            archetypeName: archetype?.name || 'archetyp'
        });
    };

    const handleMergeArchetypes = async () => {
        if (!isAdmin) {
            setError('Tylko administratorzy mogą mergować archetypy.');
            return;
        }
        if (!mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId) {
            setError('Wybierz poprawny archetyp źródłowy i docelowy.');
            return;
        }
        try {
            setError(null);
            await mergeArchetypes({
                sourceArchetypeId: mergeSourceId,
                targetArchetypeId: mergeTargetId,
                reason: mergeReason
            });
            const updatedArchetypes = await getAllArchetypes(trackerFilters);
            setArchetypes(updatedArchetypes);
            setMergeDialogOpen(false);
            setMergeSourceId('');
            setMergeTargetId('');
            setMergeReason('');
        } catch (err) {
            console.error('Błąd podczas merge archetypów:', err);
            setError('Nie udało się połączyć archetypów. Spróbuj ponownie.');
        }
    };

    const handleCopyArchetypes = async () => {
        if (!isAdmin) return;
        if (!copySourceFormat || !copySourceSetId || !trackerFilters?.format || !trackerFilters?.setId) {
            setError('Wybierz poprawny kontekst źródłowy i docelowy.');
            return;
        }
        try {
            setCopySubmitting(true);
            setError(null);
            await copyArchetypesToScope({
                sourceFormat: copySourceFormat,
                sourceSetId: copySourceSetId,
                targetFormat: trackerFilters.format,
                targetSetId: trackerFilters.setId,
                overwrite: copyOverwrite
            });
            const updatedArchetypes = await getAllArchetypes(trackerFilters);
            setArchetypes(updatedArchetypes);
            setCopyDialogOpen(false);
        } catch (err) {
            console.error('Błąd podczas kopiowania archetypów:', err);
            setError(err.message || 'Nie udało się skopiować archetypów.');
        } finally {
            setCopySubmitting(false);
        }
    };

    const handleAssignLegacyScope = async () => {
        if (!isAdmin || !trackerFilters?.format || !trackerFilters?.setId) {
            setError('Brak aktywnego kontekstu docelowego.');
            return;
        }
        try {
            setLegacyAssigning(true);
            setError(null);
            const result = await assignLegacyArchetypesScope({
                targetFormat: trackerFilters.format,
                targetSetId: trackerFilters.setId
            });
            const updatedArchetypes = await getAllArchetypes(trackerFilters);
            setArchetypes(updatedArchetypes);
            if ((result?.assigned || 0) === 0) {
                setError('Brak legacy archetypów do przypisania w tym środowisku.');
            }
        } catch (err) {
            console.error('Błąd podczas przypisywania legacy archetypów:', err);
            setError(err.message || 'Nie udało się przypisać legacy archetypów.');
        } finally {
            setLegacyAssigning(false);
        }
    };

    const handleOpenAdminMenu = (event) => {
        setAdminMenuAnchor(event.currentTarget);
    };

    const handleCloseAdminMenu = () => {
        setAdminMenuAnchor(null);
    };

    useEffect(() => {
        if (!mergeSourceId) return;
        const source = archetypes.find((entry) => entry.id === mergeSourceId);
        const target = archetypes.find((entry) => entry.id === mergeTargetId);
        if (!source || !target) return;
        const sourceColors = [...(source.ink_colors || [])].sort().join('|');
        const targetColors = [...(target.ink_colors || [])].sort().join('|');
        if (sourceColors !== targetColors) {
            setMergeTargetId('');
        }
    }, [mergeSourceId, mergeTargetId, archetypes]);

    const getInkColor = (colorName) => {
        const color = inkColors.find(c => c.value === colorName);
        return color ? color.color : '#6b7280';
    };

    const getTierBackground = (hexColor, opacity = 0.12) => {
        const hex = (hexColor || '').replace('#', '');
        if (hex.length !== 6) return `rgba(76, 29, 149, ${opacity})`;
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const portalElement = document.createElement('div');
        portalElement.setAttribute('data-archetype-dnd-portal', 'true');
        document.body.appendChild(portalElement);
        dragPortalRef.current = portalElement;

        return () => {
            if (dragPortalRef.current && dragPortalRef.current.parentNode) {
                dragPortalRef.current.parentNode.removeChild(dragPortalRef.current);
            }
            dragPortalRef.current = null;
        };
    }, []);

    const renderArchetypeOption = (archetype) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography sx={{ color: 'white', flex: 1 }}>
                {archetype.name}
            </Typography>
            {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.3 }}>
                    {archetype.ink_colors.map((color, index) => {
                        const colorData = inkColors.find(c => c.value === color);
                        return (
                            <Avatar
                                key={index}
                                src={colorData?.icon}
                                sx={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: getInkColor(color),
                                    border: `1px solid ${getInkColor(color)}`
                                }}
                            />
                        );
                    })}
                </Box>
            )}
        </Box>
    );

    const selectedSource = archetypes.find((entry) => entry.id === mergeSourceId) || null;
    const mergeTargetOptions = selectedSource
        ? archetypes.filter((entry) => {
            const sourceColors = [...(selectedSource.ink_colors || [])].sort().join('|');
            const targetColors = [...(entry.ink_colors || [])].sort().join('|');
            return sourceColors === targetColors;
        })
        : archetypes;

    const getArchetypeTierForView = useCallback((archetypeId, viewMode) => {
        if (viewMode === 'global') {
            const rawGlobal = globalTierlist?.[archetypeId];
            const globalValue = typeof rawGlobal === 'string'
                ? rawGlobal
                : rawGlobal?.globalRanking;
            return normalizeTierName(globalValue || 'not_tested');
        }

        return normalizeTierName(userTierlist?.[archetypeId] || 'not_tested');
    }, [globalTierlist, normalizeTierName, userTierlist]);

    // Funkcja do aktualizacji tierlisty użytkownika
    const updateUserTierlist = async (archetypeId, newTier) => {
        if (!currentUser) return;
        if (!rankingsAvailable) {
            setError('Zapisywanie tierlisty jest chwilowo niedostępne w tym środowisku.');
            return;
        }

        const previousTier = userTierlist?.[archetypeId];
        try {
            const normalizedTier = normalizeTierName(newTier);
            setUserTierlist((prev) => ({ ...prev, [archetypeId]: normalizedTier }));
            await setArchetypeRanking(archetypeId, normalizedTier, trackerFilters);
            const refreshed = await getArchetypeRankings(trackerFilters);
            setGlobalTierlist(refreshed?.globalRankings || {});
        } catch (err) {
            console.error('Błąd podczas aktualizacji tierlisty:', err);
            setUserTierlist((prev) => {
                const next = { ...prev };
                if (previousTier === undefined) {
                    delete next[archetypeId];
                } else {
                    next[archetypeId] = previousTier;
                }
                return next;
            });

            const message = String(err?.message || '');
            const isRankingEndpointUnavailable = message.includes('HTTP 404') || message.includes('HTTP 503');
            if (isRankingEndpointUnavailable) {
                setRankingsAvailable(false);
                setTierListView('global');
                setError('Zapisywanie tierlisty nie jest dostępne na tym środowisku produkcyjnym (brak endpointu rankingów).');
                return;
            }
            setError('Nie udało się zapisać tierlisty. Spróbuj ponownie.');
        }
    };

    // Funkcja do grupowania archetypów według tierów
    const groupArchetypesByTier = (viewMode) => {
        const grouped = {};
        tiers.forEach(tier => {
            grouped[tier.name] = [];
        });

        archetypes.forEach(archetype => {
            const tier = getArchetypeTierForView(archetype.id, viewMode);
            if (grouped[tier]) {
                grouped[tier].push(archetype);
            }
        });

        return grouped;
    };

    // Funkcja obsługi kliknięcia na archetyp
    const handleArchetypeClick = (archetypeId) => {
        navigate(`/archetype/${archetypeId}`);
    };

    // Funkcja obsługi drag & drop
    const handleDragEnd = async (result) => {
        if (tierListView === 'global' || !rankingsAvailable) {
            return;
        }
        const { destination, source, draggableId } = result;

        // Jeśli nie ma miejsca docelowego, nie rób nic
        if (!destination) {
            return;
        }

        // Jeśli archetyp został upuszczony w tym samym miejscu, nie rób nic
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        // Blokuje "sloppy click" po zakończeniu drag&drop.
        suppressCardClickUntilRef.current = Date.now() + 250;

        // Znajdź archetyp
        const archetype = archetypes.find((a) => String(a.id) === String(draggableId));
        if (!archetype) return;

        // Aktualizuj tier archetypu
        const newTier = normalizeTierName(destination.droppableId);
        await updateUserTierlist(archetype.id, newTier);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: '#7c3aed' }} />
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {!rankingsAvailable && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Ranking archetypów jest niedostępny na tym środowisku. Lista archetypów działa, ale zapis własnej tierlisty jest wyłączony.
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.8)' }}>
                        {tierListView === 'personal'
                            ? 'Przeciągaj archetypy między tierami, aby budować własną ocenę mety.'
                            : 'Widok globalny pokazuje zagregowane rankingi społeczności.'}
                    </Typography>
                    <Box sx={{ display: 'inline-flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant={tierListView === 'personal' ? 'contained' : 'outlined'}
                            onClick={() => setTierListView('personal')}
                            disabled={!rankingsAvailable}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                minWidth: 140,
                                borderRadius: '10px',
                                px: 2
                            }}
                        >
                            Moja tierlista
                        </Button>
                        <Button
                            size="small"
                            variant={tierListView === 'global' ? 'contained' : 'outlined'}
                            onClick={() => setTierListView('global')}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                minWidth: 160,
                                borderRadius: '10px',
                                px: 2
                            }}
                        >
                            Globalna tierlista
                        </Button>
                    </Box>
                </Box>
                {isAdmin && (
                    <>
                        <Button
                            variant="outlined"
                            startIcon={<MoreVertIcon />}
                            onClick={handleOpenAdminMenu}
                            sx={{
                                textTransform: 'none'
                            }}
                        >
                            Admin
                        </Button>
                        <Menu
                            anchorEl={adminMenuAnchor}
                            open={Boolean(adminMenuAnchor)}
                            onClose={handleCloseAdminMenu}
                            PaperProps={{
                                sx: {
                                    backgroundColor: 'rgba(17, 24, 39, 0.97)',
                                    border: '1px solid rgba(76, 29, 149, 0.35)',
                                    borderRadius: '10px'
                                }
                            }}
                        >
                            <MenuItem onClick={() => { handleCloseAdminMenu(); handleOpenDialog(); }}>
                                Dodaj archetyp
                            </MenuItem>
                            <MenuItem onClick={() => { handleCloseAdminMenu(); setMergeDialogOpen(true); }}>
                                Merge archetypów
                            </MenuItem>
                            <MenuItem onClick={() => { handleCloseAdminMenu(); setCopyDialogOpen(true); }}>
                                Kopiuj archetypy
                            </MenuItem>
                            <MenuItem onClick={() => { handleCloseAdminMenu(); handleAssignLegacyScope(); }} disabled={legacyAssigning}>
                                {legacyAssigning ? 'Przypisywanie...' : 'Przypisz legacy do kontekstu'}
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>


            {archetypes.length === 0 ? (
                <Card sx={{
                    background: 'rgba(17, 24, 39, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '16px',
                    p: 4
                }}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <PaletteIcon sx={{ fontSize: 64, color: 'rgba(196, 181, 253, 0.3)', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                            Brak archetypów
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mb: 3 }}>
                            Dodaj pierwszy archetyp aby rozpocząć kategoryzację talii
                        </Typography>
                        {isAdmin && (
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                            >
                                Dodaj Archetyp
                            </Button>
                        )}
                    </Box>
                </Card>
            ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {tiers.map((tier) => {
                            const groupedArchetypes = groupArchetypesByTier(tierListView);
                            const tierArchetypes = groupedArchetypes[tier.name] || [];

                            return (
                                <Paper
                                    key={tier.name}
                                    sx={{
                                        background: 'rgba(17, 24, 39, 0.52)',
                                        backdropFilter: 'blur(10px)',
                                        border: `1px solid ${getTierBackground(tier.color, 0.45)}`,
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        minHeight: 100
                                    }}
                                >
                                    <Box sx={{ display: 'flex', height: '100%' }}>
                                        {/* Tier indicator - lewa strona */}
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                minWidth: { xs: 142, sm: 156, md: 170 },
                                                height: '100%',
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                gap: 0.75,
                                                px: 1.5,
                                                py: 1.5,
                                                backgroundColor: getTierBackground(tier.color, 0.12),
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '4px',
                                                    backgroundColor: tier.color,
                                                    borderRadius: '0 2px 2px 0'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                <Typography
                                                    sx={{
                                                        color: '#f8fafc',
                                                        backgroundColor: getTierBackground(tier.color, 0.28),
                                                        border: `1px solid ${getTierBackground(tier.color, 0.6)}`,
                                                        borderRadius: '999px',
                                                        px: 1.2,
                                                        py: 0.3,
                                                        fontWeight: 800,
                                                        fontSize: '0.88rem',
                                                        lineHeight: 1,
                                                        letterSpacing: '0.02em',
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.45)'
                                                    }}
                                                >
                                                    {tier.label}
                                                </Typography>
                                                <Tooltip
                                                    title={tier.description}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: 'rgba(226, 232, 240, 0.78)',
                                                            cursor: 'help'
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'rgba(226, 232, 240, 0.82)',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {tierArchetypes.length} archetypów
                                            </Typography>
                                        </Box>

                                        {/* Droppable area dla archetypów */}
                                        <Droppable droppableId={tier.name} direction="horizontal">
                                            {(provided, snapshot) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1.5,
                                                        p: 2,
                                                        flex: 1,
                                                        alignItems: 'center',
                                                        minHeight: '100%',
                                                        backgroundColor: snapshot.isDraggingOver
                                                            ? getTierBackground(tier.color, 0.12)
                                                            : 'transparent',
                                                        borderRadius: '8px',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                >
                                                    {tierArchetypes.length > 0 ? (
                                                        tierArchetypes.map((archetype, index) => (
                                                            <Draggable
                                                                key={archetype.id}
                                                                draggableId={String(archetype.id)}
                                                                index={index}
                                                                isDragDisabled={tierListView === 'global' || !rankingsAvailable}
                                                            >
                                                                {(provided, snapshot) => {
                                                                    const draggableCard = (
                                                                        <Box
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            style={provided.draggableProps.style}
                                                                            onClick={() => {
                                                                                if (snapshot.isDragging) return;
                                                                                if (Date.now() < suppressCardClickUntilRef.current) return;
                                                                                handleArchetypeClick(archetype.id);
                                                                            }}
                                                                            sx={{
                                                                                position: 'relative',
                                                                                width: 120,
                                                                                height: 80,
                                                                                backgroundColor: snapshot.isDragging
                                                                                    ? 'rgba(0, 0, 0, 0.9)'
                                                                                    : 'rgba(17, 24, 39, 0.6)',
                                                                                border: snapshot.isDragging
                                                                                    ? `4px solid ${tier.color}`
                                                                                    : '1px solid rgba(76, 29, 149, 0.3)',
                                                                                borderRadius: '12px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
                                                                                transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                                                                                boxShadow: snapshot.isDragging
                                                                                    ? `0 20px 40px rgba(0, 0, 0, 0.8), 0 0 0 2px ${tier.color}, 0 0 20px ${tier.color}80`
                                                                                    : 'none',
                                                                                zIndex: snapshot.isDragging ? 9999 : 'auto',
                                                                                opacity: snapshot.isDragging ? 1 : 1,
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                                                                    border: `1px solid ${tier.color}`,
                                                                                    boxShadow: snapshot.isDragging
                                                                                        ? `0 20px 40px rgba(0, 0, 0, 0.8), 0 0 0 2px ${tier.color}, 0 0 20px ${tier.color}80`
                                                                                        : `0 4px 12px ${tier.color}40`
                                                                                },
                                                                                '&:active': {
                                                                                    cursor: 'grabbing'
                                                                                }
                                                                            }}
                                                                        >
                                                                        {/* Przyciski akcji dla adminów */}
                                                                        {isAdmin && (
                                                                            <Box sx={{
                                                                                position: 'absolute',
                                                                                top: 4,
                                                                                right: 4,
                                                                                display: 'flex',
                                                                                gap: 0.3,
                                                                                opacity: 0,
                                                                                transition: 'opacity 0.2s ease',
                                                                                '&:hover': {
                                                                                    opacity: 1
                                                                                }
                                                                            }}>
                                                                                <Tooltip title="Edytuj">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleOpenDialog(archetype);
                                                                                        }}
                                                                                        sx={{
                                                                                            color: 'rgba(196, 181, 253, 0.7)',
                                                                                            width: 20,
                                                                                            height: 20,
                                                                                            minWidth: 20,
                                                                                            p: 0,
                                                                                            '&:hover': {
                                                                                                color: 'rgba(196, 181, 253, 1)',
                                                                                                backgroundColor: 'rgba(124, 58, 237, 0.2)'
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <EditIcon sx={{ fontSize: 12 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Usuń">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            requestDeleteArchetype(archetype);
                                                                                        }}
                                                                                        sx={{
                                                                                            color: 'rgba(239, 68, 68, 0.7)',
                                                                                            width: 20,
                                                                                            height: 20,
                                                                                            minWidth: 20,
                                                                                            p: 0,
                                                                                            '&:hover': {
                                                                                                color: 'rgba(239, 68, 68, 1)',
                                                                                                backgroundColor: 'rgba(239, 68, 68, 0.2)'
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <DeleteIcon sx={{ fontSize: 12 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                {isSuperAdmin && (
                                                                                    <Tooltip title="Edytorzy poradnika">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setGuideEditorManagerArchetype(archetype);
                                                                                                setGuideEditorManagerOpen(true);
                                                                                            }}
                                                                                            sx={{
                                                                                                color: 'rgba(99, 179, 237, 0.7)',
                                                                                                width: 20,
                                                                                                height: 20,
                                                                                                minWidth: 20,
                                                                                                p: 0,
                                                                                                '&:hover': {
                                                                                                    color: 'rgba(99, 179, 237, 1)',
                                                                                                    backgroundColor: 'rgba(99, 179, 237, 0.2)'
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <MenuBookIcon sx={{ fontSize: 12 }} />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </Box>
                                                                        )}

                                                                        {/* Ikony kolorów atramentu */}
                                                                        {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                                                                            <Box sx={{
                                                                                display: 'flex',
                                                                                gap: 0.5,
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                mb: 0.8
                                                                            }}>
                                                                                {archetype.ink_colors.map((color, index) => {
                                                                                    const colorData = inkColors.find(c => c.value === color);
                                                                                    return (
                                                                                        <Avatar
                                                                                            key={index}
                                                                                            src={colorData?.icon}
                                                                                            sx={{
                                                                                                width: 28,
                                                                                                height: 28,
                                                                                                backgroundColor: getInkColor(color),
                                                                                                border: `2px solid ${getInkColor(color)}`
                                                                                            }}
                                                                                        />
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        )}

                                                                        {/* Nazwa archetypu */}
                                                                        <Typography variant="caption" sx={{
                                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                                            fontWeight: 600,
                                                                            fontSize: '0.8rem',
                                                                            textAlign: 'center',
                                                                            lineHeight: 1.1,
                                                                            maxWidth: '100%',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                            {archetype.name}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{
                                                                            color: 'rgba(148, 163, 184, 0.95)',
                                                                            fontSize: '0.65rem',
                                                                            mt: 0.3
                                                                        }}>
                                                                            Global: {tierNameToLabel[getArchetypeTierForView(archetype.id, 'global')] || 'Not tested'}
                                                                        </Typography>
                                                                        </Box>
                                                                    );

                                                                    if (snapshot.isDragging && dragPortalRef.current) {
                                                                        return ReactDOM.createPortal(draggableCard, dragPortalRef.current);
                                                                    }

                                                                    return draggableCard;
                                                                }}
                                                            </Draggable>
                                                        ))
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '100%',
                                                                height: 60,
                                                                color: 'rgba(255, 255, 255, 0.3)',
                                                                fontStyle: 'italic'
                                                            }}
                                                        >
                                                            Brak archetypów w tym tierze
                                                        </Box>
                                                    )}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </DragDropContext>
            )}

            {/* Add/Edit Archetype Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '20px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: 'white',
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)'
                }}>
                    {editingArchetype ? 'Edytuj Archetyp' : 'Dodaj Nowy Archetyp'}
                </DialogTitle>

                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Nazwa archetypu"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7c3aed'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgba(196, 181, 253, 0.7)'
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#7c3aed'
                                    }
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mb: 1.5 }}>
                                Wybierz maksymalnie 2 kolory atramentu:
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                p: 1.5,
                                backgroundColor: 'rgba(17, 24, 39, 0.2)',
                                borderRadius: '12px',
                                border: '1px solid rgba(76, 29, 149, 0.15)',
                                minHeight: 60,
                                alignItems: 'center'
                            }}>
                                {inkColors.map((color) => {
                                    const isSelected = formData.ink_colors.includes(color.value);
                                    const isDisabled = !isSelected && formData.ink_colors.length >= 2;

                                    return (
                                        <Tooltip key={color.value} title={color.label}>
                                            <Avatar
                                                src={color.icon}
                                                onClick={() => {
                                                    if (isDisabled) return;

                                                    if (isSelected) {
                                                        // Usuń kolor
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            ink_colors: prev.ink_colors.filter(c => c !== color.value)
                                                        }));
                                                    } else {
                                                        // Dodaj kolor
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            ink_colors: [...prev.ink_colors, color.value]
                                                        }));
                                                    }
                                                }}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    backgroundColor: color.color,
                                                    border: `3px solid ${isSelected ? '#ffffff' : 'rgba(76, 29, 149, 0.3)'}`,
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    opacity: isDisabled ? 0.3 : 1,
                                                    transition: 'all 0.3s ease',
                                                    filter: isSelected ? 'none' : 'grayscale(0.3)',
                                                    boxShadow: isSelected ? `0 0 0 2px ${color.color}, 0 0 15px ${color.color}80` : 'none',
                                                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                                    position: 'relative',
                                                    '&::before': isSelected ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: -3,
                                                        left: -3,
                                                        right: -3,
                                                        bottom: -3,
                                                        borderRadius: '50%',
                                                        border: `2px solid ${color.color}`,
                                                        animation: 'pulse 2s infinite',
                                                        '@keyframes pulse': {
                                                            '0%': {
                                                                boxShadow: `0 0 0 0 ${color.color}80`
                                                            },
                                                            '70%': {
                                                                boxShadow: `0 0 0 8px ${color.color}00`
                                                            },
                                                            '100%': {
                                                                boxShadow: `0 0 0 0 ${color.color}00`
                                                            }
                                                        }
                                                    } : {},
                                                    '&:hover': {
                                                        boxShadow: isSelected ? `0 0 0 2px ${color.color}, 0 0 20px ${color.color}90` : `0 0 8px ${color.color}40`
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1.5 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: 'rgba(196, 181, 253, 0.7)',
                            borderColor: 'rgba(76, 29, 149, 0.5)',
                            '&:hover': {
                                borderColor: 'rgba(76, 29, 149, 0.8)',
                                backgroundColor: 'rgba(76, 29, 149, 0.1)'
                            }
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.name || formData.ink_colors.length === 0}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                            borderRadius: '12px',
                            px: 3,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9, #2563eb)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(124, 58, 237, 0.4)'
                            },
                            '&:disabled': {
                                background: 'rgba(76, 29, 149, 0.3)',
                                color: 'rgba(196, 181, 253, 0.5)'
                            }
                        }}
                    >
                        {editingArchetype ? 'Zapisz Zmiany' : 'Dodaj Archetyp'}
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={confirmDeleteArchetype.open}
                title="Usunąć archetyp?"
                message={`Czy na pewno chcesz usunąć archetyp "${confirmDeleteArchetype.archetypeName}"? Tej operacji nie można cofnąć.`}
                confirmLabel="Usuń archetyp"
                onClose={() => setConfirmDeleteArchetype({ open: false, archetypeId: null, archetypeName: '' })}
                onConfirm={async () => {
                    if (confirmDeleteArchetype.archetypeId) {
                        await handleDeleteArchetype(confirmDeleteArchetype.archetypeId);
                    }
                    setConfirmDeleteArchetype({ open: false, archetypeId: null, archetypeName: '' });
                }}
            />

            <Dialog
                open={copyDialogOpen}
                onClose={() => setCopyDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '20px'
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white', fontWeight: 600 }}>
                    Kopiuj archetypy do bieżącego kontekstu
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.85)', mb: 2 }}>
                        Cel: {trackerFilters?.format || '-'} · {trackerFilters?.setId || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Format źródłowy</InputLabel>
                            <Select
                                value={copySourceFormat}
                                onChange={(event) => setCopySourceFormat(event.target.value)}
                                label="Format źródłowy"
                                sx={{ color: 'white' }}
                            >
                                <MenuItem value="core_constructed">Core Constructed</MenuItem>
                                <MenuItem value="infinity">Infinity</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Set źródłowy</InputLabel>
                            <Select
                                value={copySourceSetId}
                                onChange={(event) => setCopySourceSetId(event.target.value)}
                                label="Set źródłowy"
                                sx={{ color: 'white' }}
                            >
                                {expansionSets.map((setItem) => (
                                    <MenuItem key={setItem.id} value={setItem.id}>
                                        {setItem.name || setItem.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={copyOverwrite}
                                    onChange={(event) => setCopyOverwrite(event.target.checked)}
                                />
                            }
                            label="Nadpisz duplikaty nazw/kolorów w celu"
                            sx={{ color: 'rgba(196, 181, 253, 0.9)' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCopyDialogOpen(false)}>
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleCopyArchetypes}
                        variant="contained"
                        disabled={copySubmitting || !copySourceSetId}
                        sx={{ background: 'linear-gradient(135deg, #10b981, #3b82f6)' }}
                    >
                        {copySubmitting ? 'Kopiowanie...' : 'Kopiuj'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={mergeDialogOpen}
                onClose={() => setMergeDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Merge archetypów</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Archetyp źródłowy</InputLabel>
                        <Select
                            value={mergeSourceId}
                            label="Archetyp źródłowy"
                            onChange={(event) => setMergeSourceId(event.target.value)}
                        >
                            {archetypes.map((archetype) => (
                                <MenuItem key={archetype.id} value={archetype.id}>
                                    {renderArchetypeOption(archetype)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Archetyp docelowy</InputLabel>
                        <Select
                            value={mergeTargetId}
                            label="Archetyp docelowy"
                            onChange={(event) => setMergeTargetId(event.target.value)}
                        >
                            {mergeTargetOptions.map((archetype) => (
                                <MenuItem key={archetype.id} value={archetype.id}>
                                    {renderArchetypeOption(archetype)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Powód merge (opcjonalnie)"
                        value={mergeReason}
                        onChange={(event) => setMergeReason(event.target.value)}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMergeDialogOpen(false)}>Anuluj</Button>
                    <Button
                        variant="contained"
                        onClick={handleMergeArchetypes}
                        disabled={!mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId}
                    >
                        Połącz
                    </Button>
                </DialogActions>
            </Dialog>

            <GuideEditorManager
                open={guideEditorManagerOpen}
                onClose={() => setGuideEditorManagerOpen(false)}
                archetype={guideEditorManagerArchetype}
            />
        </Box>
    );
}

export default ArchetypeManager;
